// RFC-1146 REST client for per-variant favourite/dislike markers.
//
// All requests route through `apiFetch` (api/client.ts), which resolves the
// configured API base URL and attaches `credentials: 'include'`. That is
// mandatory rather than incidental: per CLAUDE.md → Production Request
// Routing, a bare `fetch()` silently breaks the `DREAMWRIGHT_PROJECT` session
// affinity cookie behind the GCE load balancer, which degrades into
// round-robin (stale statuses, YDoc write races). Never use bare `fetch` here.
//
// Responses are validated through the generated `parseVariantMarkersResponse`
// at the schema boundary — no `as T` cast (CLAUDE.md #3), and backend drift
// fails loudly here instead of reaching the hook as trusted-shape data.

import { apiFetch, type ApiFetchOptions } from './client'
import {
  parseVariantMarkersResponse,
  type ApiValue,
  type VariantMarker,
  type VariantMarkersResponse,
} from '../generated/variantMarkers'

/** Typed error carrier so callers can narrow on HTTP status. */
export class VariantMarkerRequestError extends Error {
  readonly status: number
  readonly body: ApiValue | null
  constructor(status: number, body: ApiValue | null) {
    super(`variant-marker request failed: ${status}`)
    this.name = 'VariantMarkerRequestError'
    this.status = status
    this.body = body
  }
}

async function _fetch(
  path: string,
  init?: ApiFetchOptions,
): Promise<VariantMarkersResponse> {
  const resp = await apiFetch(path, init)
  if (!resp.ok) {
    const body: ApiValue | null = await resp
      .json()
      .catch(() => ({ error: `http_${resp.status}` }))
    throw new VariantMarkerRequestError(resp.status, body)
  }
  const raw: ApiValue | null = await resp.json()
  return parseVariantMarkersResponse(raw)
}

function _basePath(projectId: string): string {
  return `/projects/${encodeURIComponent(projectId)}/variant-markers`
}

/**
 * List markers for a project, optionally narrowed to one node.
 */
export async function getVariantMarkers(
  projectId: string,
  opts?: { nodeId?: string },
): Promise<VariantMarkersResponse> {
  const params = new URLSearchParams()
  if (opts?.nodeId) params.set('node_id', opts.nodeId)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  return _fetch(`${_basePath(projectId)}${suffix}`)
}

/**
 * Upsert one marker. Neutral is not expressible here — clearing is a DELETE.
 */
export async function putVariantMarker(
  projectId: string,
  body: { nodeId: string; localPath: string; marker: VariantMarker },
): Promise<VariantMarkersResponse> {
  return _fetch(_basePath(projectId), {
    method: 'PUT',
    body: JSON.stringify({
      node_id: body.nodeId,
      local_path: body.localPath,
      marker: body.marker,
    }),
    // Last-writer-wins upsert on a natural PK, so a retried PUT is harmless.
    // Left on default retry behaviour, unlike the rules client (whose PUT
    // carries an `expected_version` that a retry would invalidate).
  })
}

/**
 * Clear one marker, returning the variant to neutral.
 *
 * Sends the identity as query params rather than a body: DELETE-with-body is
 * poorly supported across proxies.
 */
export async function deleteVariantMarker(
  projectId: string,
  body: { nodeId: string; localPath: string },
): Promise<VariantMarkersResponse> {
  const params = new URLSearchParams({
    node_id: body.nodeId,
    local_path: body.localPath,
  })
  return _fetch(`${_basePath(projectId)}?${params.toString()}`, {
    method: 'DELETE',
  })
}
